"use client";
import { useEffect } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import { ButtonCta, LiveChatButton } from "@/components";

const features = [
  {
    title: "Real Humans, Real Craft",
    description:
      "Experienced editors, designers, and writers — never template output.",
    icon: "M12 3l2.5 5 5.5.8-4 3.9.9 5.5L12 21l-4.9 2.6.9-5.5-4-3.9L10 8l2-5Z",
  },
  {
    title: "100% Rights & Royalties Are Yours",
    description:
      "Full copyright and every dollar earned go directly to you.",
    icon: "M20 21a8 8 0 0 0-16 0M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z",
  },
  {
    title: "Every Format, Covered",
    description:
      "Print, eBook, and audiobook, plus marketing assets, coordinated as one project.",
    icon: "M4 5h16v14H4zM4 9h16M9 5v14",
  },
  {
    title: "Distribution That Actually Fits",
    description:
      "We map your book to the retailers and platforms suited to your genre and goals.",
    icon: "M3 11l18-7-7 18-2.5-7.5L3 11Z",
  },
  {
    title: "Support That Doesn't End at Launch",
    description:
      "We stay with you for your next edition, release, or campaign.",
    icon: "M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8Z",
  },
  {
    title: "Transparent Pricing, No Surprises",
    description:
      "Clear, itemized quotes and a signed agreement before any work begins.",
    icon: "M3 3v18h18M7 15l3-4 3 3 5-7",
  },
];

export default function WhyChooseUs() {
  useEffect(() => {
    AOS.init({ duration: 800, once: true });
  }, []);

  return (
    <section className="relative overflow-hidden bg-white px-4 py-20 md:px-8">
      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-16">
        {/* Sticky left column */}
        <div className="lg:sticky lg:top-28 lg:h-fit">
          <span className="eyebrow flex items-center gap-3 text-gold-deep">
            <span className="h-px w-8 bg-gold/70" />
            Why Authors Choose Us
          </span>
          <h2 className="mt-5 font-display text-[clamp(1.9rem,4vw,3rem)] font-semibold leading-[1.1] text-ink">
            You Keep the Rights. You Keep the Royalties. Always.
          </h2>
          <p className="mt-5 text-ink-soft">
            You poured the work into writing your book. We pour the same care into
            publishing it — with full creative control staying in your hands from
            first draft to final file.
          </p>

          <div className="relative mt-8 overflow-hidden rounded-3xl border border-ink/10 shadow-editorial">
            <img loading="lazy" decoding="async" src="/images/achievement-img.webp" alt="Authors in a bookstore" className="h-64 w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/70 to-transparent" />
            <div className="absolute bottom-5 left-5 flex items-center gap-3 text-white">
              <img loading="lazy" decoding="async" src="/images/Badge.webp" alt="" className="h-12 w-12 object-contain" />
              <div>
                <p className="font-display text-lg font-semibold">Trusted by published, award-winning authors</p>
                <p className="text-xs text-white/80">Recognized at the International Book Excellence Awards</p>
              </div>
            </div>
          </div>

          <div className="mt-8 flex flex-wrap gap-4">
            <LiveChatButton buttonText="Ask Us Anything" classStyle="text-white bg-brand-600 hover:bg-brand-700 h-12" />
            <ButtonCta buttonText="Book a Free Consultation" classStyle="text-ink bg-transparent border border-ink/15 hover:border-gold/60 hover:bg-cream h-12" />
          </div>
        </div>

        {/* Feature rows */}
        <div className="flex flex-col divide-y divide-ink/10">
          {features.map((f, i) => (
            <div
              key={f.title}
              data-aos="fade-up"
              data-aos-delay={i * 60}
              className="group flex items-start gap-5 py-6 transition-colors"
            >
              <span className="flex-shrink-0 font-display text-2xl font-semibold text-ink/20 transition-colors group-hover:text-gold">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-700 transition-all duration-300 group-hover:-translate-y-1 group-hover:bg-brand-600 group-hover:text-white">
                <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6">
                  <path d={f.icon} stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
              <div>
                <h3 className="font-display text-xl font-semibold text-ink">{f.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-ink-soft">{f.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
